import React, { useState, useEffect, useMemo } from 'react';
import TemplatePreview from '../TemplatePreview';
import DateFnsUtils from '@date-io/date-fns';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';

import {
  useGetAllCampaignsQuery,
  useGetCampaignTemplatesQuery,
  useGetAllSMSTemplatesQuery,
  useGetAllEmailTemplatesQuery,
  useGetAllPrintTemplatesQuery,
  useAddCampaignTemplatesMutation,
  useUpdateCampaignTemplatesMutation,
} from '../../apis/backend';


import {
  LinearProgress,
  Typography,
  Box,
  Divider,
  Button,
  Paper,
  Grid,
  FormControl,
  FormHelperText,
  TextField,
  ButtonGroup
} from '@material-ui/core';


const useStyles = makeStyles(theme => ({
  formControl: {
    margin: theme.spacing(0)
  },
  container: {
    width: '100%',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    '& .MuiFormControl-root': {
      paddingBottom: theme.spacing(2),
    },
    '& .MuiTypography-h6': {
      textTransform: 'uppercase',
      fontSize: 14,
      color: theme.palette.grey[600],
      paddingBottom: theme.spacing(2),
      borderBottom: '1px solid',
      borderBottomColor: theme.palette.grey[300],
      marginBottom: theme.spacing(3)
    }
  },
  columnContainer: {
    padding: theme.spacing(0, 0)
  }
}));

const OneCampaign = ({ match }) => {
  const [templatesForm, setTemplatesForm] = useState();

  const chainId = useSelector(state => state.doris.selectedChain);
  const siteId = useSelector(state => state.doris.selectedSite);

  const [addCampaignTemplates] = useAddCampaignTemplatesMutation();
  const [updateCampaignTemplates] = useUpdateCampaignTemplatesMutation();

  const skipGetAllCampaigns = !(chainId && siteId);
  const { data: campaignsResult, isLoading: isLoadingCampaigns, isFetching: isFetchingCampaigns, error: errorCampaigns } = useGetAllCampaignsQuery({ chainId, siteId }, { skip: skipGetAllCampaigns });

  const skipGetCampaignTemplates = !(chainId && siteId);
  const { data: allCampaignTemplates, isLoading: isLoadingCampaignTemplates, error: isCampaignTemplatesError } = useGetCampaignTemplatesQuery({ chainId, siteId }, { skip: skipGetCampaignTemplates });

  const skipGetAllSMSTemplates = !(chainId && siteId);
  const { data: smsTemplateList, isLoadingSMSTemplates, errorSMS } = useGetAllSMSTemplatesQuery({ chainId, siteId }, { skip: skipGetAllSMSTemplates });

  const skipGetAllEmailTemplates = !(chainId && siteId);
  const { data: emailTemplateList, isLoadingEmailTemplates, errorEmail } = useGetAllEmailTemplatesQuery({ chainId, siteId }, { skip: skipGetAllEmailTemplates });

  const skipSkipGetAllPrintTemplates = !(chainId && siteId);
  const { data: printTemplateList, isLoadingPrint, errorPrint } = useGetAllPrintTemplatesQuery({ chainId, siteId }, { skip: skipSkipGetAllPrintTemplates });

  const isLoading = isFetchingCampaigns || isLoadingCampaigns || isLoadingCampaignTemplates || isLoadingSMSTemplates || isLoadingEmailTemplates || isLoadingPrint;
  const error = errorCampaigns || isCampaignTemplatesError || errorSMS || errorEmail || errorPrint;

  if (error) console.error(JSON.stringify(error.data));

  const campaignTemplates = useMemo(() => {
    return allCampaignTemplates?.find(
      ct => ct.campaign_id == match.params.id && ct.site_id === siteId) || {};
  }, [allCampaignTemplates]);

  const [smsInputValue, setSMSinputValue] = useState();
  const [emailInputValue, setEmailInputValue] = useState();
  const [printInputValue, setPrintInputValue] = useState();
  const [templateType, setTemplateType] = useState();
  const [openSMSDialog, setOpenSMSDialog] = useState(false);
  const [campaign, setCampaign] = useState();

  const classes = useStyles();
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const history = useHistory();


  useEffect(() => {
    if (!campaignsResult?.campaigns) return;
    const campaignFound = campaignsResult.campaigns.find(c => c.id == match.params.id);
    if (campaignFound) setCampaign(campaignFound);
  }, [campaignsResult]);

  useEffect(() => {
    if (chainId && siteId && campaign) {
      const campaignBelongsToContext = campaign.sites.find(s => s.chainId == chainId && s.siteId == siteId);
      if (!campaignBelongsToContext) return history.push('/campaigns');
    }
  }, [chainId, siteId, campaign]);

  useEffect(() => {
    if (!templatesForm?.campaign_id) setTemplatesForm(campaignTemplates);
  }, [campaignTemplates]);

  const findSMStemplate = () => {
    return smsTemplateList
      ?.find(s => (s.id == templatesForm.sms_template_id));
  };

  const findEmailTemplate = () => {
    return emailTemplateList
      ?.find(e => e.id == templatesForm.email_template_id);
  };

  const findPrintTemplate = () => {
    return printTemplateList
      ?.find(p => p.id == templatesForm.print_template_id);
  };

  const handleCloseSMSDialog = () => {
    setOpenSMSDialog(false);
  };

  const previewTemplate = (templateType) => {
    let data = null;
    switch (templateType) {
    case 'sms':
      data = findSMStemplate() || smsTemplateList?.find(t => t.ongoing);
      break;
    case 'email':
      data = findEmailTemplate() || emailTemplateList?.find(t => t.ongoing);
      break;
    default:
      break;
    }
    return data;
  };

  const attachTemplate = async (entityType, campaignTemplates) => {
    try {
      const isNewCampaignTemplates = !campaignTemplates?.campaign_id;
      let templatesFormDiff;
      switch (entityType) {
      case 'sms': {
        const { sms_template_id, sms_date } = templatesForm;
        templatesFormDiff = {
          sms_template_id,
          sms_date: moment(sms_date).utc()
        };
        break;
      }
      case 'email': {
        const { email_template_id, email_date } = templatesForm;
        templatesFormDiff = {
          email_template_id,
          email_date
        };
        break;
      }
      case 'print':
        break;
      }

      let payload, result;
      if (isNewCampaignTemplates) {
        const { campaign_id } = templatesForm;
        const base = {
          campaign_id
        };
        payload = { ...base, ...templatesFormDiff };
        result = await addCampaignTemplates({ id: campaign.id, payload, chainId, siteId }).unwrap();
      } else {
        payload = { ...campaignTemplates, ...templatesFormDiff };
        result = await updateCampaignTemplates({ id: campaign.id, payload, chainId, siteId }).unwrap();
      }
      const templateAssigned = result[`${entityType}_template_id`];
      templateAssigned
        ? snackbar.showMessage(t(`${entityType}-template-attached-to-campaign-success`))
        : snackbar.showMessage(t(`${entityType}-template-detached-to-campaign-success`));
    } catch (error) {
      console.log(error);
      snackbar.showMessage(t(`${entityType}-template-attached-to-campaign-fail`));
    }
  };

  const disableSMS = () => {
    const saveNewCampaignTemplates = !Object.keys(campaignTemplates).length
      && templatesForm.sms_template_id;
    const smsTemplateChanged = templatesForm.sms_template_id
      && campaignTemplates.sms_template_id != templatesForm.sms_template_id;
    const removeSMStemplate = !templatesForm.sms_template_id
      && campaignTemplates.sms_template_id;

    const formHasNewData = saveNewCampaignTemplates || smsTemplateChanged || removeSMStemplate;
    return !formHasNewData;
  };

  const disableEmail = () => {
    const saveNewCampaignTemplates = !Object.keys(campaignTemplates).length
      && templatesForm.email_template_id;
    const emailTemplateChanged = templatesForm.email_template_id
      && campaignTemplates.email_template_id != templatesForm.email_template_id;
    const removeEmailtemplate = !templatesForm.email_template_id
      && campaignTemplates.email_template_id;

    const formHasNewData = saveNewCampaignTemplates || emailTemplateChanged || removeEmailtemplate;
    return !formHasNewData;
  };

  const disablePrint = () => {
    const saveNewCampaignTemplates = !Object.keys(campaignTemplates).length
      && templatesForm.print_template_id;
    const printTemplateChanged = templatesForm.print_template_id
      && campaignTemplates.print_template_id != templatesForm.print_template_id;
    const removePrinttemplate = !templatesForm.print_template_id
      && campaignTemplates.print_template_id;

    const formHasNewData = saveNewCampaignTemplates || printTemplateChanged || removePrinttemplate;
    return !formHasNewData;
  };

  const defaultTemplate = (entityType) => {
    if (entityType == 'sms' && smsTemplateList?.length) {
      const template = smsTemplateList.find(t => t.ongoing);
      if (template) return `${template.title} (${template.id})`;
    } else if (entityType == 'email' && emailTemplateList?.length) {
      const template = emailTemplateList.find(t => t.ongoing);
      if (template) return `${template.title} (${template.id})`;
    } else if (entityType == 'print' && printTemplateList?.length) {
      const template = printTemplateList.find(t => t.ongoing);
      if (template) return `${template.title} (${template.id})`;
    }

    return '';
  };

  const smsMarketing = () => {
    return (
      <Paper elevation={0}>
        <Box p={3}>
          <Typography variant="h6">SMS Marketing</Typography>
          <Box className={classes.columnContainer}>
            <FormControl fullWidth>
              <TextField
                id="default-sms-template"
                variant="outlined"
                label="Default Template"
                value={defaultTemplate('sms')}
                disabled />
              <FormHelperText>
                Default template used for when ongoing process is enabled.
              </FormHelperText>
            </FormControl>
            <Autocomplete
              options={smsTemplateList || []}
              value={findSMStemplate() || null}
              onChange={(event, option) => {
                const formData = { sms_template_id: option ? option.id : null };
                setTemplatesForm({ ...templatesForm, ...formData });
              }}
              inputValue={smsInputValue ? smsInputValue : ''}
              onInputChange={(event, newInputValue) => setSMSinputValue(newInputValue)}
              renderOption={option => (
                <span>{option.title} ({option.id})</span>
              )}
              getOptionLabel={option => `${option.title} (${option.id})` || ''}
              getOptionSelected={(option, value) => {
                return option.id == value.id;
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="SMS Template" />
              )} />
            <FormControl fullWidth>
              <Button
                fullWidth
                color="primary"
                variant="outlined"
                disabled={!templatesForm?.sms_template_id && !previewTemplate('sms')}
                onClick={() => {
                  setTemplateType('sms');
                  setOpenSMSDialog(true);
                }}>
                Preview Template
              </Button>
            </FormControl>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                emptyLabel=""
                // value={templatesForm ? templatesForm.sms_date : null}
                onChange={async smsDateValue => {
                  const stringDate = await moment(smsDateValue).startOf('day').toISOString();
                  const formData = { sms_date: stringDate };
                  setTemplatesForm({ ...templatesForm, ...formData });
                }}
                clearable={true}
                disableToolbar
                inputVariant="outlined"
                format="dd.MM.yyyy"
                inputformat='dd.MM.yy'
                id="sms-publish-date"
                disabled
                label="Publishing Date"
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
                fullWidth />
            </MuiPickersUtilsProvider>
            <Button
              className={classes.outlineButton}
              fullWidth
              color="primary"
              variant="outlined"
              disabled={disableSMS()}
              onClick={() => attachTemplate('sms', campaignTemplates)}>
              {t('save')}
            </Button>
            <ButtonGroup variant="outlined" color="primary" fullWidth>
              <Button disabled style={{ display: 'none' }}>Execute Campaign</Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Paper>
    );
  };

  const emailMarketing = () => {
    return (
      <Paper elevation={0}>
        <Box p={3}>
          <Typography variant="h6">Email Marketing</Typography>
          <Box className={classes.columnContainer}>
            <FormControl fullWidth>
              <TextField
                id="default-email-template"
                variant="outlined"
                label="Default Template"
                value={defaultTemplate('email')}
                disabled />
              <FormHelperText>
                Default template used for when ongoing process is enabled.
              </FormHelperText>
            </FormControl>
            <Autocomplete
              options={emailTemplateList || []}
              value={findEmailTemplate() || null}
              onChange={(event, option) => {
                const formData = { email_template_id: option ? option.id : null };
                setTemplatesForm({ ...templatesForm, ...formData });
              }}
              inputValue={emailInputValue ? emailInputValue : ''}
              onInputChange={(event, newInputValue) => setEmailInputValue(newInputValue)}
              renderOption={option => (
                <span>{option.title} ({option.id})</span>
              )}
              getOptionLabel={option => `${option.title} (${option.id})` || ''}
              getOptionSelected={(option, value) => {
                return option.id == value.id;
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Email Template" />
              )} />
            <FormControl fullWidth>
              <Button
                fullWidth
                color="primary"
                variant="outlined"
                disabled={!templatesForm?.email_template_id && !previewTemplate('email')}
                onClick={() => {
                  setTemplateType('email');
                  setOpenSMSDialog(true);
                }}>
                Preview Template
              </Button>
            </FormControl>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                emptyLabel=""
                // value={templatesForm ? templatesForm.email_date : null}
                onChange={async emailDateValue => {
                  const stringDate = await moment(emailDateValue).startOf('day').toISOString();
                  const formData = { sms_date: stringDate };
                  setTemplatesForm({ ...templatesForm, ...formData });
                }}
                clearable={true}
                disableToolbar
                inputVariant="outlined"
                format="dd.MM.yyyy"
                inputformat='dd.MM.yy'
                id="email-publish-date"
                disabled
                label="Publishing Date"
                KeyboardButtonProps={{
                  'aria-label': 'change date',
                }}
                fullWidth />
            </MuiPickersUtilsProvider>
            <Button
              className={classes.outlineButton}
              fullWidth
              color="primary"
              variant="outlined"
              disabled={disableEmail()}
              onClick={() => attachTemplate('email', campaignTemplates)}>
              {t('save')}
            </Button>
            <ButtonGroup variant="outlined" color="primary" fullWidth>
              <Button disabled style={{ display: 'none' }}>Execute Campaign</Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Paper>
    );
  };

  const printMarketing = () => {
    return (
      <Paper elevation={0}>
        <Box p={3}>
          <Typography variant="h6">Print Marketing</Typography>
          <Box className={classes.columnContainer}>
            <FormControl fullWidth>
              <TextField
                id="default-print-template"
                variant="outlined"
                label="Default Template"
                value={defaultTemplate('print')}
                disabled />
              <FormHelperText>
                Default template used for when ongoing process is enabled.
              </FormHelperText>
            </FormControl>
            <Autocomplete
              disabled
              options={printTemplateList}
              value={findPrintTemplate() || null}
              onChange={(event, option) => {
                const formData = { print_template_id: option ? option.id : null };
                setTemplatesForm({ ...templatesForm, ...formData });
              }}
              inputValue={printInputValue ? printInputValue : ''}
              onInputChange={(event, newInputValue) => setPrintInputValue(newInputValue)}
              renderOption={option => (
                <span>{option.title} ({option.id})</span>
              )}
              getOptionLabel={option => `${option.title} (${option.id})` || ''}
              getOptionSelected={(option, value) => {
                return option.id == value.id;
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" label="Print Template" />
              )} />
            <FormControl fullWidth>
              <Button
                fullWidth
                color="primary"
                variant="outlined"
                disabled={!templatesForm.print_template_id && !previewTemplate('print')}
                onClick={() => {
                  setTemplateType('print');
                  setOpenSMSDialog(true);
                }}>
                Preview Template
              </Button>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={6}>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    emptyLabel=""
                    // value={templatesForm ? templatesForm.print_date : null}
                    onChange={async printDateValue => {
                      const stringDate = await moment(printDateValue).startOf('day').toISOString();
                      const formData = { sms_date: stringDate };
                      setTemplatesForm({ ...templatesForm, ...formData });
                    }}
                    clearable={true}
                    disableToolbar
                    inputVariant="outlined"
                    format="dd.MM.yyyy"
                    inputformat='dd.MM.yy'
                    id="print-publish-date"
                    disabled
                    label="Publishing Date"
                    KeyboardButtonProps={{
                      'aria-label': 'change date',
                    }}
                    fullWidth />
                </MuiPickersUtilsProvider>
              </Grid>
              <Grid item xs={12} lg={6}>
                <TextField
                  disabled
                  variant="outlined"
                  fullWidth
                  label="Postal Codes" />
              </Grid>
            </Grid>
            <Button
              className={classes.outlineButton}
              fullWidth
              color="primary"
              variant="outlined"
              disabled={disablePrint()}
              onClick={() => attachTemplate('email', campaignTemplates)}>
              {t('save')}
            </Button>
            <ButtonGroup variant="outlined" color="primary" fullWidth>
              <Button disabled style={{ display: 'none' }}>Execute Campaign</Button>
            </ButtonGroup>
          </Box>
        </Box>
      </Paper>
    );
  };

  if (!campaign) return <LinearProgress />;

  return (
    <>
      <Box>
        <Typography variant="h4">{campaign.name}</Typography>
        <Divider />
        <Box height="4px">
          {isLoading && <LinearProgress />}
        </Box>
        <Grid container className={classes.container} spacing={3}>
          <Grid xs={12} lg={4} item>
            {smsMarketing()}
          </Grid>
          <Grid xs={12} lg={4} item>
            {emailMarketing()}
          </Grid>
          <Grid xs={12} lg={4} item>
            {printMarketing()}
          </Grid>
        </Grid>
        <TemplatePreview
          campaign={campaign}
          open={openSMSDialog}
          data={previewTemplate(templateType)}
          handleClose={handleCloseSMSDialog}
          templateType={templateType} />
      </Box>
    </>
  );
};

export default OneCampaign;
